"use client";

import { useState, useMemo } from "react";
import { DownloadIcon, PlayIcon, CheckmarkIcon } from "./icons";
import Link from "next/link";
import Image from "next/image";
import {
  parseMarkdown,
  getCourseDetails,
  handleDownloadBrochure,
} from "./utils";
import UserFormModal from "@/components/UserFormModal";

export default function CourseHeroSection({ course, courseData }) {
  const [playVideo, setPlayVideo] = useState(false);
  const details = getCourseDetails(courseData);
  // Download Brochure
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [open, setOpen] = useState(false);
  const [shouldDownload, setShouldDownload] = useState(false);

  // Helper to resolve relative URLs using NEXT_PUBLIC_SERVER_URL
  const resolveUrl = (u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    const base = (process.env.NEXT_PUBLIC_SERVER_URL || "").replace(/\/$/, "");
    if (!base) return u;
    if (u.startsWith("/")) return `${base}${u}`;
    return `${base}/${u}`;
  };

  // Gather possible media sources
  const mediaCandidates = useMemo(() => {
    return {
      mediaUrl: course?.CourseMediaUrl || null,
      // possible image fields
      imageUrl:
        course?.CourseMediaImage?.url ||
        null,
      // poster image for video
      poster:
        course?.CourseMediaImage?.url || null,
    };
  }, [course]);

  const mediaUrl = mediaCandidates.mediaUrl || mediaCandidates.imageUrl || null;
  const posterUrl = mediaCandidates.poster || null;

  const isYouTube = (u) => /(?:youtube\.com|youtu\.be)/i.test(u);
  const isVideoExt = (u) => /\.(mp4|webm|ogg|mov)(?:\?|$)/i.test(u) || isYouTube(u);
  const isImageExt = (u) => /\.(png|jpe?g|webp|gif|svg)(?:\?|$)/i.test(u);

  const resolvedMediaUrl = mediaUrl ? resolveUrl(mediaUrl) : null;
  const resolvedPoster = posterUrl ? resolveUrl(posterUrl) : null;

  // If no poster provided and media is YouTube, compute YouTube thumbnail as fallback
  // Robust YouTube ID extractor that handles multiple URL forms (watch?v=, embed/, youtu.be/, shorts/)
  const getYouTubeId = (u) => {
    if (!u) return null;
    try {
      const url = new URL(u);
      const host = url.hostname.replace(/^www\./i, "").toLowerCase();

      // short url: youtu.be/ID
      if (host === "youtu.be") {
        const id = url.pathname.split("/").filter(Boolean)[0];
        return id || null;
      }

      // youtube domains
      if (host === "youtube.com" || host.endsWith(".youtube.com") || host === "m.youtube.com") {
        // v= param
        const v = url.searchParams.get("v");
        if (v) return v;
        // path based IDs: /embed/ID or /shorts/ID
        const parts = url.pathname.split("/").filter(Boolean);
        if (parts[0] === "embed" || parts[0] === "shorts") return parts[1] || null;
        return null;
      }

      // fallback regex for anything else
      const m = u.match(/(?:v=|\/embed\/|\.be\/|\/shorts\/)([^&?\/]+)/i);
      return m ? m[1] : null;
    } catch (e) {
      const m = u.match(/(?:v=|\/embed\/|\.be\/|\/shorts\/)([^&?\/]+)/i);
      return m ? m[1] : null;
    }
  };

  const youtubeId = resolvedMediaUrl && isYouTube(resolvedMediaUrl) ? getYouTubeId(resolvedMediaUrl) : null;
  const youtubeThumb = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
  const finalPoster = resolvedPoster || youtubeThumb || null;

  // Download Brochure
  const handleCourseDownload = (courseData) => {
    setSelectedCourse(courseData);
    setShouldDownload(true);
    setOpen(true);
  }

  const handleFormSuccess = () => {
    if (shouldDownload && selectedCourse) {
      handleDownloadBrochure(selectedCourse);
      setShouldDownload(false);
      setSelectedCourse(null);
    }
  };

  return (
    <>
      <UserFormModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSuccess={handleFormSuccess}
        shouldDownload={shouldDownload}
      />
      <section className="py-[30px] md:py-[60px] bg-[#F3F9FD]">
        <div className="page-container grid lg:grid-cols-2 gap-[74px] md:gap-[60px] items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <h1 className="mb-[17px]">{course.name}</h1>

            {/* Course Details List */}
            <div className="mb-[16px] md:mb-[25px]">
              {details.map((detail, index) => (
                <div
                  key={index}
                  className="flex gap-3 mt-[10px] md:mt-[12px] items-start"
                >
                  <div className="w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckmarkIcon />
                  </div>
                  <p className="text-black text-base text-[20px]">
                    {parseMarkdown(detail)}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}

            <div className="flex flex-wrap gap-4">
              {course?.BrochurePdf?.url && (
                <button
                  onClick={() => {
                    handleCourseDownload(course);
                  }}

                  className="flex items-center gap-2 px-6 py-3 border-[1px] border-black text-black font-semibold hover:bg-black hover:text-white transition-colors cursor-pointer w-full justify-center md:w-auto"
                  disabled={!course?.BrochurePdf?.url}
                >
                  Download Brochure
                  <DownloadIcon />
                </button>
              )}
              <Link href="https://application.msu.edu.in/campus-programme-registration" className="flex items-center gap-2 px-6 py-3 bg-[#FFA201] text-black font-semibold transition-colors cursor-pointer w-full justify-center md:w-auto">
                Enrol Now
              </Link>
            </div>
          </div>

          <div className="relative">
            {resolvedMediaUrl ? (
              isImageExt(resolvedMediaUrl) ? (
                <div className="relative w-full shadow-2xl overflow-hidden aspect-[16/9] bg-black">
                  <Image
                    src={resolvedMediaUrl}
                    alt={course?.name || "Course media"}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="object-cover"
                  />
                </div>
              ) : isYouTube(resolvedMediaUrl) ? (
                // Embed YouTube
                !playVideo ? (
                  <div className="relative w-full shadow-2xl overflow-hidden aspect-[16/9] bg-black">
                    {finalPoster ? (
                      <Image src={finalPoster} alt="poster" fill priority className="object-cover" />
                    ) : null}
                    <button
                      onClick={() => setPlayVideo(true)}
                      className="absolute inset-0 flex items-center justify-center"
                      aria-label="Play course intro video"
                    >
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-all transform hover:scale-110 shadow-lg">
                        <PlayIcon />
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="relative w-full shadow-2xl overflow-hidden aspect-[16/9] bg-black">
                    <iframe
                      title="course-video"
                      src={resolvedMediaUrl.includes("embed") ? resolvedMediaUrl : resolvedMediaUrl.replace("watch?v=", "embed/")}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )
              ) : (
                // Treat as generic video file
                !playVideo ? (
                  <div className="relative w-full shadow-2xl overflow-hidden aspect-[16/9] bg-black">
                    {finalPoster ? (
                      <Image src={finalPoster} alt="poster" fill priority className="object-cover" />
                    ) : (
                      // If no poster is available for an S3-hosted video, show a paused muted preview (no autoplay) and show a play overlay instead
                      <video
                        src={resolvedMediaUrl}
                        poster={finalPoster || undefined}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                      />
                    )}

                    <button
                      onClick={() => setPlayVideo(true)}
                      className="absolute inset-0 flex items-center justify-center"
                      aria-label="Play course intro video"
                    >
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-all transform hover:scale-110 shadow-lg">
                        <PlayIcon />
                      </div>
                    </button>
                  </div>
                ) : (
                  <video
                    src={resolvedMediaUrl}
                    className="w-full h-full"
                    controls
                    autoPlay
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    Your browser does not support the video tag.
                  </video>
                )
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white">
                {/* No media available */}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
